import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { FeaturesApi } from "@features/shared/api/course-data.api";
import { IAppContract, routePathNameChanged } from "@features/app/model";
import { parseISO, differenceInCalendarDays } from "date-fns";
import { IUpsellUIContract } from ".";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { COMPLETE_BUNDLE_UPSELL_IDS, IUpsellContract } from "../model";
import { interval, reset } from "patronum";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { INotificationContract } from "@features/notification/model";
import { IMetadataContract } from "@features/student/metadata/model";
import { IPlatformContextContract } from "@features/launch-context/model";

const TARGET_IDS = new Set(["ai_tools", "ai_tools_sale"]);
const PAYMENT_CHECK_FLAG_KEY = "ai_tools_payment_check_active";
const AI_TOOLS_BOUGHT_KEY = "ai_tools_bought";
const RE_ENGAGEMENT_GIFT_BONUS = 10;
const RE_ENGAGEMENT_GIFT_TEST_MODE_KEY = "re_engagement_gift_test_mode";
const RE_ENGAGEMENT_GIFT_DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const RE_ENGAGEMENT_GIFT_TEST_TTL_MS = 10 * 60 * 1000;

const isReEngagementGiftTestModeEnabled = (): boolean => {
    if (typeof window === "undefined") return false;

    const rawValue = localStorage.getItem(RE_ENGAGEMENT_GIFT_TEST_MODE_KEY);
    if (!rawValue) return false;

    const normalizedValue = rawValue.trim().toLowerCase();
    return ["1", "true", "yes", "on", "enabled", "test"].includes(normalizedValue);
};

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const bannerClicked = createEvent<{ placement: "profile" | "rail" }>();
const modalOpened = createEvent();
const modalClosed = createEvent();
const paywallModalOpened = createEvent();
const paywallModalClosed = createEvent();
const incrementPromptCount = createEvent();
const assistantPromptLimitReached = createEvent();
const resetStores = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();
const aiToolsStatusResolved = createEvent<UpsellStatus>();

const $isModalOpen = createStore(false)
    .on(modalOpened, () => true)
    .on(modalClosed, () => false);
const $isAiToolsModalPaywallOpen = createStore(false)
    .on(paywallModalOpened, () => true)
    .on(paywallModalClosed, () => false);
const $assistantLimitDelayActive = createStore(false)
    .on(assistantPromptLimitReached, () => true)
    .on(paywallModalOpened, () => false)
    .reset(resetStores, routePathNameChanged);
const $promptsCount = createStore<number>(-1).reset(resetStores, routePathNameChanged);
const $featureRemainingPromptsCount = createStore<number>(0).reset(resetStores, routePathNameChanged);
// Tracks the original base limit from the server (not overridden by bonus).
// Only set from getFeaturesFx so that increment responses with extended limit don't pollute it.
const $baseFeatureTotalPromptsCount = createStore<number>(0).reset(resetStores, routePathNameChanged);
const $reEngagementGiftBonus = createStore<number>(0);
const $totalPromptsCount = combine(
    [$baseFeatureTotalPromptsCount, $reEngagementGiftBonus],
    ([baseFeatureTotal, giftBonus]) => baseFeatureTotal + giftBonus
);
const $remainingPromptsCount = combine(
    {
        prompts: $promptsCount,
        featureRemaining: $featureRemainingPromptsCount,
        baseFeatureTotal: $baseFeatureTotalPromptsCount,
        giftBonus: $reEngagementGiftBonus
    },
    ({ prompts, featureRemaining, baseFeatureTotal, giftBonus }) => {
        // Before the first API response — fall back to remaining_count from the backend + bonus
        if (prompts < 0) return featureRemaining + giftBonus;
        const used = Math.max(0, prompts);
        return Math.max(baseFeatureTotal + giftBonus - used, 0);
    }
);
const $upsellIds = createStore<string[]>([]);
const $aiToolsPackStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);
const $aiToolsNeedPresented = createStore(false);
const $isPaymentCheckActive = createStore(false);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 5000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const getFeaturesFx = createEffect(async () => {
    const response = await FeaturesApi.getFeatures("ai_tools_prompts_limit");
    return response;
});

const setPaymentCheckFlagFx = createEffect(async (value: boolean) => {
    if (typeof window !== "undefined") {
        localStorage.setItem(PAYMENT_CHECK_FLAG_KEY, value ? "true" : "false");
    }
    return value;
});

const clearPaymentCheckFlagFx = createEffect(async () => {
    if (typeof window !== "undefined") {
        localStorage.removeItem(PAYMENT_CHECK_FLAG_KEY);
    }
    return false;
});

const getPaymentCheckFlagFx = createEffect(() => {
    if (typeof window !== "undefined") {
        return localStorage.getItem(PAYMENT_CHECK_FLAG_KEY) === "true";
    }
    return false;
});

const incrementFeatureFx = createEffect(async ({ featureLimit }: { featureLimit?: number }) => {
    const response = await FeaturesApi.incrementFeature("ai_tools_prompts_limit", featureLimit);
    return response;
});

const $isPromptsDataLoaded = createStore(false).reset(resetStores, routePathNameChanged);

const saveFlagForAiToolsToStorageFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const firstLogin = localStorage.getItem("first_login");
        if (firstLogin === "true") {
            localStorage.setItem("aiToolsBought", "false");
        }
    }
});

const setPrevUrlInStorageFx = createEffect((url: string) => {
    if (typeof window !== "undefined") {
        localStorage.setItem("prevUrl", url);
    }
});

sample({
    source: {
        baseFeatureTotal: $baseFeatureTotalPromptsCount,
        giftBonus: $reEngagementGiftBonus
    },
    clock: incrementPromptCount,
    fn: ({ baseFeatureTotal, giftBonus }) => {
        if (giftBonus > 0) {
            // Extend the backend limit to include bonus so it allows increments past base
            return { featureLimit: baseFeatureTotal + giftBonus };
        }
        return { featureLimit: undefined };
    },
    target: incrementFeatureFx
});

sample({
    clock: [getFeaturesFx.doneData, incrementFeatureFx.doneData],
    fn: (data) => data.current_value,
    target: $promptsCount
});

sample({
    clock: [getFeaturesFx.doneData, incrementFeatureFx.doneData],
    fn: (data) => data.remaining_count,
    target: $featureRemainingPromptsCount
});

sample({
    clock: getFeaturesFx.doneData,
    fn: (data) => data.feature_limit,
    target: $baseFeatureTotalPromptsCount
});

sample({
    clock: [getFeaturesFx.done, getFeaturesFx.fail, incrementFeatureFx.done, incrementFeatureFx.fail],
    fn: () => true,
    target: $isPromptsDataLoaded
});

sample({
    clock: [getFeaturesFx.doneData, incrementFeatureFx.doneData],
    filter: (data) => {
        const now = new Date();
        const lastRequestDate = parseISO(data.counter_date);

        const dayDiff = differenceInCalendarDays(now, lastRequestDate);
        return dayDiff >= 1;
    },
    fn: () => -1,
    target: $promptsCount
});

sample({
    clock: [getFeaturesFx.doneData, incrementFeatureFx.doneData],

    filter: (data) => {
        const now = new Date();
        const lastRequestDate = parseISO(data.counter_date);

        const dayDiff = differenceInCalendarDays(now, lastRequestDate);
        return dayDiff >= 1;
    },
    fn: (data) => data.feature_limit,
    target: $featureRemainingPromptsCount
});

sample({
    clock: paywallModalClosed,
    fn: () => false,
    target: $aiToolsNeedPresented
});

const contract = new ModelContract(
    {
        $isModalOpen,
        $isAiToolsModalPaywallOpen,
        $assistantLimitDelayActive,
        $promptsCount,
        $remainingPromptsCount,
        $totalPromptsCount,
        $isPromptsDataLoaded,
        $aiToolsPackStatus,
        $isBuyButtonDisabled
    },
    {
        buyButtonClicked,
        buyButtonReset,
        bannerClicked,
        modalOpened,
        modalClosed,
        paywallModalOpened,
        paywallModalClosed,
        incrementPromptCount,
        assistantPromptLimitReached
    },
    {},
    {}
);

export type IAiToolsPackContract = typeof contract;

export class AiToolsPackModule extends ModuleDescription<IAiToolsPackContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public appModule: IAppContract,
        public subscriptionsModule: ISubscriptionContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public routesModule: IAppRouteUnitsContract,
        public notificationModule: INotificationContract,
        public metadataModule: IMetadataContract,
        public platformContextModule: IPlatformContextContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // The chain holds upsell ids for every provider (truegate plans are
        // resolved on the server now), so the check needs no catalog.
        sample({
            source: {
                upsells: this.upsellModule.stores.$upsells,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.upsells.length > 0,
            fn: (s) => {
                const upsellIds = s.upsells.filter((u) => TARGET_IDS.has(u.upsellId)).map((u) => u.upsellId);
                if (upsellIds.length === 0) return upsellIds;
                const result = [...upsellIds];
                if (!result.includes("bundle")) {
                    result.push("bundle");
                }
                // The Complete Bundles grant this pack too, the drama / film ones included.
                for (const bundleId of COMPLETE_BUNDLE_UPSELL_IDS) {
                    if (!result.includes(bundleId)) result.push(bundleId);
                }
                return result;
            },
            target: $upsellIds
        });

        sample({
            clock: [this.routesModule.stores.assistant.$isActive, this.routesModule.stores.aiTools.$isActive],
            filter: (isActive) => isActive,
            target: getPaymentCheckFlagFx
        });

        sample({
            clock: [this.routesModule.stores.aiTools.$isActive, this.routesModule.stores.lesson.$isActive],
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                aiToolsPackStatus: $aiToolsPackStatus,
                promptsCount: $promptsCount
            },
            filter: (s, isActive) =>
                isActive &&
                ["solidgate", "ecommpay", "truegate"].includes(s.paymentSystem) &&
                (s.aiToolsPackStatus === "Fail" || s.aiToolsPackStatus === null) &&
                s.promptsCount === -1,
            target: getFeaturesFx
        });

        sample({
            clock: [getPaymentCheckFlagFx.doneData, setPaymentCheckFlagFx.doneData, clearPaymentCheckFlagFx.doneData],
            fn: (value) => value,
            target: $isPaymentCheckActive
        });

        sample({
            source: {
                upsellIds: $upsellIds,
                history: this.routerModule.stores.$history,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                metadata: this.metadataModule.stores.$metadata,
                aiToolsBought: $aiToolsPackStatus,
                isApp: this.platformContextModule.stores.$isApp
            },
            filter: (s) => {
                if (s.isApp) return false;
                if (!s.isAssistantRoute || s.upsellIds.length === 0) return false;
                const raw = s.metadata[AI_TOOLS_BOUGHT_KEY];

                const prevUrl = s.history.at(-2) ?? "";

                return !prevUrl.includes("/lesson/") && !raw && s.aiToolsBought === "Fail";
            },
            fn: () => true,
            target: $aiToolsNeedPresented
        });

        sample({
            clock: paywallModalClosed,
            source: { metadata: this.metadataModule.stores.$metadata },
            filter: (s) => !Boolean(s.metadata[AI_TOOLS_BOUGHT_KEY]),
            fn: () => ({
                key: AI_TOOLS_BOUGHT_KEY,
                value: new Date().toISOString()
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            source: {
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive
            },
            filter: (s) => s.isAssistantRoute || s.isLessonRoute,
            fn: () => "ai_tools",
            target: this.upsellModule.events.currentUpsellIdChanged
        });

        sample({
            clock: modalClosed,
            fn: () => "/upsell/ai_tools_sale",
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "ai_tools",
            target: modalOpened
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        sample({
            clock: bannerClicked,
            source: $aiToolsPackStatus,
            filter: (status) => status === "Fail",
            fn: () => "/profile",
            target: setPrevUrlInStorageFx
        });

        sample({
            clock: bannerClicked,
            source: $aiToolsPackStatus,
            fn: (status) => (status === "Fail" ? "/upsell/ai_tools" : "/assistant"),
            target: this.routerModule.events.routerPush
        });

        sample({
            source: {
                upsellIds: $upsellIds,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the packs on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    s.isAssistantRoute ||
                    s.isAiToolsRoute ||
                    s.isLessonRoute ||
                    (s.upsellPath !== null &&
                        (s.upsellPath.includes("ai_tools") || s.upsellPath.includes("bundle")))) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem),
            fn: (s, { result }) => {
                if (s.paymentSystem === "apple") return "Success";

                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack whose order sits in 3ds_verify reads as
                // "Fail" until the bank dialog (opened by the upsell model on
                // this page) is done. It is in progress, not failed: keep the
                // 5s poll going and keep the paywall away from the dialog.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: [$aiToolsPackStatus, aiToolsStatusResolved]
        });

        sample({
            source: {
                aiToolsPackStatus: $aiToolsPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) =>
                (s.aiToolsPackStatus === "Success" || s.aiToolsPackStatus === "Processing") &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("ai_tools") &&
                !s.firstLoginFlag,
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                aiToolsPackStatus: $aiToolsPackStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) =>
                s.aiToolsPackStatus === "Fail" &&
                s.isUpsellRoute &&
                s.upsellPath !== null &&
                s.upsellPath.includes("ai_tools"),
            target: buyButtonReset
        });

        sample({
            clock: $aiToolsPackStatus,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $aiToolsPackStatus,
            source: {
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive
            },
            filter: (s, p) => (s.isAssistantRoute || s.isAiToolsRoute) && p === "Processing",
            fn: () => true,
            target: setPaymentCheckFlagFx
        });

        sample({
            clock: $aiToolsPackStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        sample({
            clock: $aiToolsPackStatus,
            source: $isPaymentCheckActive,
            filter: (isActive, status) => isActive && (status === "Success" || status === "Fail"),
            fn: (_, status) => ({
                message: status === "Success" ? "Payment successful" : "Payment error",
                status: status === "Success" ? "upsell payment successful" : "upsell payment error"
            }),
            target: this.notificationModule.events.toastShow
        });

        sample({
            clock: $aiToolsPackStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: clearPaymentCheckFlagFx
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: aiToolsStatusResolved,
            source: {
                aiToolsNeedPresented: $aiToolsNeedPresented,
                isApp: this.platformContextModule.stores.$isApp
            },
            filter: (s, status) => status === "Fail" && s.aiToolsNeedPresented && !s.isApp,
            target: paywallModalOpened
        });

        // A bank dialog opens while the paywall is up (both are full-screen
        // modals; the later one wins): close the paywall so the 3D Secure
        // check stays reachable.
        sample({
            clock: this.upsellModule.stores.$upsell3dsChallenge.updates,
            source: $isAiToolsModalPaywallOpen,
            filter: (isPaywallOpen, challenge) => isPaywallOpen && challenge !== null,
            fn: () => undefined,
            target: paywallModalClosed
        });

        sample({
            clock: $aiToolsPackStatus,
            filter: (status) => status === "Fail",
            target: [getFeaturesFx, buyButtonReset]
        });

        sample({
            clock: $aiToolsPackStatus,
            filter: (status) => status === "Success",
            target: resetStores
        });

        sample({
            clock: this.metadataModule.stores.$metadata,
            fn: (metadata) => {
                if (metadata.re_engagement_gift_claimed !== "true") return 0;
                const expiresAt = metadata.re_engagement_gift_expires_at;
                if (typeof expiresAt !== "string") return 0;
                const expires = Date.parse(expiresAt);
                if (!Number.isFinite(expires)) return 0;

                const isTestMode = isReEngagementGiftTestModeEnabled();
                const effectiveExpiresAt = isTestMode
                    ? expires - RE_ENGAGEMENT_GIFT_DEFAULT_TTL_MS + RE_ENGAGEMENT_GIFT_TEST_TTL_MS
                    : expires;

                if (effectiveExpiresAt <= Date.now()) return 0;
                return RE_ENGAGEMENT_GIFT_BONUS;
            },
            target: $reEngagementGiftBonus
        });

        sample({
            clock: this.upsellUiModule.events.skipButtonClicked,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null && s.currentUpsellId.startsWith("ai_tools"),
            target: saveFlagForAiToolsToStorageFx
        });

        sample({
            clock: paywallModalOpened,
            fn: () => "ai_tools",
            target: this.upsellModule.events.currentUpsellIdChanged
        });

        // The modal renders the ai_tools offer from /students/upsells-prices,
        // which the upsell model requests on the assistant / ai-tools / lesson
        // routes — no provider catalog. Only the legacy ecommpay chain still
        // needs its plan list.
        sample({
            clock: paywallModalOpened,
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                ecommpayPlans: this.paymentModule.stores.$ecommpayPlans
            },
            filter: (s) => s.paymentSystem === "ecommpay" && s.ecommpayPlans.length === 0,
            target: this.paymentModule.events.getEcommpayAnonPlans
        });

        sample({
            clock: [
                this.upsellModule.effects.registerEcommpayUpsellFx.doneData,
                this.upsellModule.effects.registerTruegateUpsellFx.doneData,
                this.upsellModule.events.upsellChargeSucceeded,
                this.upsellModule.events.upsellUpiPaymentSucceeded,
                this.upsellModule.effects.chargeAppleUpsellFx.doneData
            ],
            source: {
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive
            },
            filter: (s) => s.isAssistantRoute || s.isAiToolsRoute || s.isLessonRoute,
            target: [buyButtonReset, paywallModalClosed]
        });

        reset({
            clock: routePathNameChanged,
            target: [$isModalOpen, $aiToolsPackStatus, $aiToolsNeedPresented, $isCheckRequested, $isPaymentCheckActive]
        });

        reset({
            clock: paywallModalClosed,
            target: [$isCheckRequested]
        });
    }
}
